import { useState } from 'react'
import { Search, Filter, Package, Loader2, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useProducts, Product } from '@/lib/data'

interface ProductsViewProps {
  client: SupabaseClient | null
}

const NUTRISCORE_COLORS: Record<string, string> = {
  a: 'bg-green-500 text-white',
  b: 'bg-lime-500 text-white',
  c: 'bg-yellow-500 text-black',
  d: 'bg-orange-500 text-white',
  e: 'bg-red-500 text-white',
}

export function ProductsView({ client }: ProductsViewProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [nutriscoreFilter, setNutriscoreFilter] = useState<string>('')
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const limit = 24

  const { products, total, isLoading, error } = useProducts(client, {
    page,
    limit,
    search,
    nutriscoreGrade: nutriscoreFilter || undefined,
  })

  const totalPages = Math.ceil(total / limit)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Package size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view products</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-main)]">Products</h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            {total.toLocaleString()} products in database
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            placeholder="Search by name, brand, or barcode..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </form>

        <div className="flex items-center gap-2">
          <span className="text-[var(--text-muted)] text-sm">Nutri-Score:</span>
          {['a', 'b', 'c', 'd', 'e'].map(grade => (
            <button
              key={grade}
              onClick={() => {
                setNutriscoreFilter(nutriscoreFilter === grade ? '' : grade)
                setPage(1)
              }}
              className={`w-8 h-8 rounded-lg font-bold uppercase ${
                nutriscoreFilter === grade
                  ? NUTRISCORE_COLORS[grade]
                  : 'bg-[var(--bg-element)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {grade}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Products Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-muted)]">
          No products found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map((product) => (
            <div
              key={product.code}
              onClick={() => setSelectedProduct(product)}
              className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden hover:border-[var(--primary)] transition-colors cursor-pointer group"
            >
              <div className="h-32 bg-[var(--bg-element)] relative flex items-center justify-center">
                {product.image_small_url || product.image_url ? (
                  <img
                    src={product.image_small_url || product.image_url || ''}
                    alt={product.product_name}
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <Package size={32} className="text-[var(--text-muted)] opacity-30" />
                )}
                {product.nutriscore_grade && (
                  <div className={`absolute top-2 right-2 w-8 h-8 rounded-lg font-bold uppercase flex items-center justify-center text-sm ${NUTRISCORE_COLORS[product.nutriscore_grade.toLowerCase()] || 'bg-gray-500 text-white'}`}>
                    {product.nutriscore_grade}
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="text-[var(--text-main)] font-medium text-sm line-clamp-2 mb-1">
                  {product.product_name || 'Unknown Product'}
                </h4>
                <p className="text-[var(--text-muted)] text-xs line-clamp-1">
                  {product.brands || 'Unknown Brand'}
                </p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border)]">
                  <span className="text-[var(--text-muted)] text-xs font-mono">{product.code}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total.toLocaleString()} products
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  )
}

function ProductDetailModal({ product, onClose }: { product: Product; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex gap-6">
            <div className="w-48 h-48 bg-[var(--bg-element)] rounded-xl flex items-center justify-center flex-shrink-0">
              {product.image_url ? (
                <img src={product.image_url} alt={product.product_name} className="w-full h-full object-contain p-2" />
              ) : (
                <Package size={48} className="text-[var(--text-muted)] opacity-30" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[var(--text-main)] mb-2">
                {product.product_name || 'Unknown Product'}
              </h2>
              <p className="text-[var(--text-muted)] mb-4">{product.brands || 'Unknown Brand'}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {product.nutriscore_grade && (
                  <div className={`px-3 py-1 rounded-lg font-bold uppercase text-sm ${NUTRISCORE_COLORS[product.nutriscore_grade.toLowerCase()] || 'bg-gray-500 text-white'}`}>
                    Nutri-Score {product.nutriscore_grade}
                  </div>
                )}
                {product.nova_group && (
                  <div className="px-3 py-1 rounded-lg bg-[var(--bg-element)] text-[var(--text-muted)] text-sm">
                    Nova {product.nova_group}
                  </div>
                )}
                {product.ecoscore_grade && (
                  <div className="px-3 py-1 rounded-lg bg-[var(--bg-element)] text-[var(--text-muted)] text-sm">
                    Eco-Score {product.ecoscore_grade.toUpperCase()}
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-muted)]">Barcode:</span>
                  <span className="text-[var(--text-main)] font-mono">{product.code}</span>
                </div>
                {product.brand_owner && (
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Brand Owner:</span>
                    <span className="text-[var(--text-main)]">{product.brand_owner}</span>
                  </div>
                )}
                {product.categories && (
                  <div>
                    <span className="text-[var(--text-muted)]">Categories:</span>
                    <p className="text-[var(--text-main)] text-xs mt-1 line-clamp-3">{product.categories}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--border)]">
            <a
              href={`https://world.openfoodfacts.org/product/${product.code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-element)] text-[var(--text-muted)] rounded-lg hover:bg-[var(--bg-hover)]"
            >
              <ExternalLink size={16} />
              View on OpenFoodFacts
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
